import { Platform, StyleSheet } from "react-native";
import { useColorScheme } from "../design-system/colorScheme";
import { type Theme, createTheme, lightTheme } from "../design-system/theme";

const ROW_HEIGHT = 72;

// Styles are theme-reactive: light vs dark palettes flip via the
// react-native useColorScheme hook (web reads document data-theme via
// react-native-web's Appearance shim; iOS/Android read system).
//
// IMPORTANT: every consumer must call `useChatListStyles()` instead of
// destructuring a static `styles` object — the static export below is
// kept as a deprecated fallback for any RN tests that snapshot off it,
// but new code should not import it.
export function useChatListStyles() {
	const scheme = useColorScheme();
	return chatListStylesFor(createTheme(scheme));
}

export function chatListStylesFor(theme: Theme) {
	return StyleSheet.create({
		container: {
			flex: 1,
			backgroundColor: theme.colors.surface.primary,
		},
		row: {
			flexDirection: "row",
			alignItems: "center",
			paddingHorizontal: theme.spacing.lg,
			paddingVertical: theme.spacing.md,
			backgroundColor: theme.colors.surface.primary,
			borderBottomWidth: StyleSheet.hairlineWidth,
			borderBottomColor: theme.colors.border.secondary,
			height: ROW_HEIGHT,
			cursor: Platform.select({ web: "pointer", default: "auto" }) as any,
		},
		avatarContainer: {
			marginRight: theme.spacing.md,
		},
		avatar: {
			width: 48,
			height: 48,
			borderRadius: theme.radii.pill,
			backgroundColor: theme.colors.accent.indigo,
			alignItems: "center",
			justifyContent: "center",
		},
		avatarText: {
			fontSize: 20,
			fontWeight: "600",
			color: "#FFFFFF",
		},
		contentContainer: {
			flex: 1,
			justifyContent: "center",
		},
		topRow: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "baseline",
			marginBottom: theme.spacing.xxs,
		},
		bottomRow: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
		},
		nameText: {
			flex: 1,
			fontSize: theme.typography.fontSize.body,
			fontWeight: `${theme.typography.fontWeight.semibold}` as any,
			color: theme.colors.text.primary,
			marginRight: theme.spacing.sm,
		},
		messageText: {
			flex: 1,
			fontSize: theme.typography.fontSize.footnote,
			fontWeight: `${theme.typography.fontWeight.regular}` as any,
			color: theme.colors.text.secondary,
			marginRight: theme.spacing.sm,
		},
		timestampText: {
			fontSize: theme.typography.fontSize.caption,
			fontWeight: `${theme.typography.fontWeight.regular}` as any,
			color: theme.colors.text.tertiary,
		},
		unreadBadge: {
			backgroundColor: theme.colors.accent.indigo,
			borderRadius: theme.radii.pill,
			paddingHorizontal: theme.spacing.sm,
			paddingVertical: theme.spacing.xxs,
			minWidth: 20,
			height: 20,
			alignItems: "center",
			justifyContent: "center",
		},
		unreadBadgeText: {
			fontSize: theme.typography.fontSize.caption,
			fontWeight: "600",
			color: "#FFFFFF",
		},
		emptyContainer: {
			flex: 1,
			alignItems: "center",
			justifyContent: "center",
			paddingHorizontal: theme.spacing.xl,
		},
		emptyTitle: {
			fontSize: theme.typography.fontSize.title,
			fontWeight: "600",
			color: theme.colors.text.primary,
			marginBottom: theme.spacing.sm,
			textAlign: "center",
		},
		emptySubtitle: {
			fontSize: theme.typography.fontSize.body,
			fontWeight: "400",
			color: theme.colors.text.secondary,
			textAlign: "center",
			lineHeight:
				theme.typography.lineHeight.relaxed * theme.typography.fontSize.body,
		},
		errorContainer: {
			flex: 1,
			alignItems: "center",
			justifyContent: "center",
			paddingHorizontal: theme.spacing.xl,
		},
		errorTitle: {
			fontSize: theme.typography.fontSize.title,
			fontWeight: "600",
			color: theme.colors.text.primary,
			marginBottom: theme.spacing.sm,
			textAlign: "center",
		},
		errorMessage: {
			fontSize: theme.typography.fontSize.body,
			fontWeight: "400",
			color: theme.colors.text.secondary,
			textAlign: "center",
			marginBottom: theme.spacing.lg,
			lineHeight:
				theme.typography.lineHeight.relaxed * theme.typography.fontSize.body,
		},
		errorButton: {
			backgroundColor: theme.colors.accent.indigo,
			borderRadius: theme.radii.md,
			paddingHorizontal: theme.spacing.xl,
			paddingVertical: theme.spacing.md,
		},
		errorButtonText: {
			fontSize: theme.typography.fontSize.body,
			fontWeight: "600",
			color: "#FFFFFF",
		},
		skeletonRow: {
			flexDirection: "row",
			alignItems: "center",
			paddingHorizontal: theme.spacing.lg,
			paddingVertical: theme.spacing.md,
			borderBottomWidth: StyleSheet.hairlineWidth,
			borderBottomColor: theme.colors.border.secondary,
			height: ROW_HEIGHT,
		},
		skeletonAvatar: {
			width: 48,
			height: 48,
			borderRadius: theme.radii.pill,
			backgroundColor: theme.colors.surface.tertiary,
			marginRight: theme.spacing.md,
		},
		skeletonContent: {
			flex: 1,
		},
		skeletonTitleLine: {
			height: 16,
			backgroundColor: theme.colors.surface.tertiary,
			borderRadius: theme.radii.xs,
			marginBottom: theme.spacing.sm,
			width: "70%",
		},
		skeletonPreviewLine: {
			height: 14,
			backgroundColor: theme.colors.surface.tertiary,
			borderRadius: theme.radii.xs,
			width: "90%",
		},
		refreshControlWeb: {
			alignItems: "center",
			paddingVertical: theme.spacing.md,
		},
		refreshControlWebText: {
			fontSize: theme.typography.fontSize.footnote,
			color: theme.colors.text.secondary,
		},
		fabContainer: {
			position: "absolute",
			right: theme.spacing.lg,
			bottom: theme.spacing.lg,
		},
		fab: {
			width: 56,
			height: 56,
			borderRadius: theme.radii.pill,
			backgroundColor: theme.colors.accent.indigo,
			alignItems: "center",
			justifyContent: "center",
			...Platform.select({
				web: {
					cursor: "pointer",
					boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
				} as any,
				default: {
					shadowColor: "#000",
					shadowOffset: { width: 0, height: 4 },
					shadowOpacity: 0.3,
					shadowRadius: 8,
					elevation: 8,
				},
			}),
		},
		fabText: {
			fontSize: 24,
			color: "#FFFFFF",
		},
		emptyCTA: {
			backgroundColor: theme.colors.accent.indigo,
			borderRadius: theme.radii.md,
			paddingHorizontal: theme.spacing.xl,
			paddingVertical: theme.spacing.md,
			marginTop: theme.spacing.lg,
		},
		emptyCTAText: {
			fontSize: theme.typography.fontSize.body,
			fontWeight: "600",
			color: "#FFFFFF",
		},
	});
}

// Deprecated: kept only so the existing snapshot/unit tests that
// destructure `styles` at module load keep compiling. New code must
// use `useChatListStyles()` (theme-reactive).
export const styles = chatListStylesFor(lightTheme);

export const ROW_HEIGHT_CONSTANT = ROW_HEIGHT;
